import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setToken, clearToken } from '../api/client';
import { authApi } from '../api/services';
import type { User, AuthResponse, UserGoal, PasswordResetConfirm } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithPassword: (phone: string, password: string) => Promise<AuthResponse>;
  signup: (firstName: string, lastName: string, phone: string, password: string, email?: string) => Promise<AuthResponse>;
  resetPassword: (data: PasswordResetConfirm) => Promise<AuthResponse>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setGoal: (goal: UserGoal) => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi.getMe()
        .then(setUser)
        .catch(() => clearToken())
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const loginWithPassword = async (phone: string, password: string): Promise<AuthResponse> => {
    const response = await authApi.loginWithPassword({ phone, password });
    setToken(response.token);
    setUser(response.user);
    return response;
  };

  const signup = async (firstName: string, lastName: string, phone: string, password: string, email?: string): Promise<AuthResponse> => {
    const response = await authApi.signup({ first_name: firstName, last_name: lastName, phone, password, email: email || undefined });
    setToken(response.token);
    setUser(response.user);
    return response;
  };

  const resetPassword = async (data: PasswordResetConfirm): Promise<AuthResponse> => {
    const response = await authApi.confirmPasswordReset(data);
    setToken(response.token);
    setUser(response.user);
    return response;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const refreshUser = async () => {
    const updatedUser = await authApi.getMe();
    setUser(updatedUser);
  };

  const setGoal = async (goal: UserGoal) => {
    const updatedUser = await authApi.setGoal(goal);
    setUser(updatedUser);
  };

  const completeOnboarding = async () => {
    const updatedUser = await authApi.completeOnboarding();
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        loginWithPassword,
        signup,
        resetPassword,
        logout,
        refreshUser,
        setGoal,
        completeOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
